<template>
  <aside class="tx-drawer">
    <!-- Header -->
    <div class="tx-drawer-header">
      <div class="d-flex align-items-center" style="gap: 8px; margin-bottom: 12px;">
        <div class="ml-auto d-flex" style="gap: 4px;">
          <button class="tx-icon-btn" @click="onClose" title="Close"><i class="fa fa-times"></i></button>
        </div>
      </div>

      <div v-if="patient" class="d-flex align-items-center" style="gap: 12px;">
        <div class="tx-drawer-avatar" :style="patientAvatarStyle">
          {{ patientInitials }}
        </div>
        <div>
          <div class="tx-drawer-name">
            {{ patient.fName }} {{ patient.lName }}
            <span
              v-if="patientStatusBadge"
              class="tx-status-badge"
              :title="`Marked ${patientStatusBadge} in Open Dental`"
            >{{ patientStatusBadge }}</span>
          </div>
          <div class="tx-drawer-meta">
            <span v-if="patient.chartNumber">{{ patient.chartNumber }} · </span>
            DOB {{ formatDate(patient.birthdate) }}
          </div>
        </div>
      </div>

      <!-- Plan badges -->
      <div v-if="plans.length > 0" class="tx-plan-badges">
        <button
          v-for="plan in plans"
          :key="plan.treatPlanNum"
          class="tx-plan-badge"
          :class="{ active: plan.treatPlanNum === selectedTreatPlanNum }"
          :disabled="loading"
          @click="onSelectPlan(plan.treatPlanNum)"
        >
          <span v-if="plan.status === 1" class="tx-active-dot"></span>
          <span class="tx-plan-badge-label">
            {{ plan.heading || (plan.status === 1 ? 'Active Plan' : 'Saved Plan') }}
          </span>
          <span v-if="chipDate(plan)" class="tx-plan-badge-date">{{ chipDate(plan) }}</span>
          <span v-if="plan.status === 1" class="tx-active-suffix">Active</span>
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading && !patient" class="tx-drawer-loading">
      <i class="fa fa-spinner fa-spin"></i>
      <div>Loading…</div>
    </div>

    <!-- Error -->
    <div v-if="error" class="tx-drawer-error">{{ error }}</div>

    <!-- Body -->
    <div v-if="patient" class="tx-drawer-body tx-scroll">
      <!-- Plan Heading -->
      <div class="tx-section">
        <div class="tx-section-label">Plan Heading</div>
        <div class="tx-section-content" style="font-size: 13px; line-height: 1.5;">
          {{ selectedPlan && selectedPlan.heading ? selectedPlan.heading : '—' }}
        </div>
        <!-- Offices that never rename a saved plan tell them apart by DateTP, not the
             heading. Always shown here (even when the heading IS the date) — the label
             is what gives the number meaning. -->
        <div v-if="selectedPlanDateTP" class="tx-section-subtext">
          Treatment planned {{ formatDate(selectedPlanDateTP) }}
        </div>
      </div>

      <!-- Financials -->
      <div class="tx-section">
        <div class="tx-section-label">Financials</div>
        <div class="tx-finbox">
          <div class="tx-finrow">
            <span class="tx-finrow-label">Total</span>
            <span class="tx-finrow-value tx-finrow-bold tx-tab-num">
              {{ selectedPlan ? formatMoney(selectedPlan.total) : '—' }}
            </span>
          </div>
          <div class="tx-finrow">
            <span class="tx-finrow-label">Procedures</span>
            <span class="tx-finrow-value tx-tab-num">
              {{ selectedPlan ? selectedPlan.procedureCount : '—' }}
            </span>
          </div>
          <div class="tx-finrow" v-if="selectedPlan && selectedPlan.lastTPActivity">
            <span class="tx-finrow-label">Last activity</span>
            <span class="tx-finrow-value">{{ formatDate(selectedPlan.lastTPActivity) }}</span>
          </div>
        </div>
      </div>

      <!-- Internal Note -->
      <div class="tx-section">
        <div class="d-flex align-items-center" style="margin-bottom: 8px;">
          <span class="tx-section-label" style="margin-bottom: 0;">Internal Note</span>
          <span class="ml-auto tx-section-status" v-if="saving">Saving…</span>
        </div>
        <textarea
          v-model="internalNoteEdit"
          class="tx-note-textarea"
          :class="{ 'tx-note-empty': !internalNoteEdit }"
          placeholder="Add a note for your team — not visible to the patient…"
          rows="3"
          :disabled="!selectedTreatPlanNum"
          @blur="onSaveNote"
        ></textarea>
      </div>

    </div>

    <!-- Footer actions -->
    <div v-if="patient" class="tx-drawer-footer">
      <button
        class="tx-btn flex-grow-1"
        :disabled="!selectedTreatPlanNum"
        title="Open the 3-pane Builder to customize this plan"
        @click="onBuild"
      >
        <i class="fa fa-pencil-square-o"></i> Build
      </button>
      <button
        class="tx-btn-primary flex-grow-1"
        :disabled="!selectedTreatPlanNum"
        title="Present this plan chairside now"
        @click="onPresent"
      >
        <i class="fa fa-desktop"></i> Present
      </button>
      <button
        v-if="!isTouchDevice"
        class="tx-btn tx-btn-tablet"
        :disabled="!selectedTreatPlanNum"
        title="Show a QR code to present on a tablet"
        @click="showTablet = true"
      >
        <i class="fa fa-qrcode"></i> Present on Tablet
      </button>
    </div>

    <PresentOnTabletModal
      v-if="showTablet && patient && selectedTreatPlanNum"
      :pat-num="patient.patNum"
      :treat-plan-num="selectedTreatPlanNum"
      :patient-name="`${patient.fName || ''} ${patient.lName || ''}`.trim()"
      :patient-dob="patient.birthdate ? formatDate(patient.birthdate) : ''"
      :plan-heading="selectedPlan && selectedPlan.heading ? selectedPlan.heading : ''"
      :plan-total="selectedPlan ? formatMoney(selectedPlan.total) : ''"
      @close="showTablet = false"
    />
  </aside>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import moment from 'moment';
import PresentOnTabletModal from './PresentOnTabletModal.vue';
import { isTouchPrimaryDevice } from '@/utils/deviceType.js';
import { patientStatusLabel } from '../constants/patientStatus.js';

export default {
  name: 'TxPresenterDrawer',
  components: { PresentOnTabletModal },

  data() {
    return {
      internalNoteEdit: '',
      lastSavedNote: '',
      showTablet: false,
    };
  },

  computed: {
    ...mapState('txPresenter', { drawer: (state) => state.drawer }),
    // Hide the QR hand-off on a tablet/phone — just use Present directly.
    isTouchDevice() { return isTouchPrimaryDevice(); },

    patient() { return this.drawer.patient; },
    plans() { return this.drawer.plans; },
    selectedTreatPlanNum() { return this.drawer.selectedTreatPlanNum; },
    selectedPlan() { return this.drawer.selectedPlan; },
    internalNote() { return this.drawer.internalNote; },
    loading() { return this.drawer.loading; },
    saving() { return this.drawer.saving; },
    error() { return this.drawer.error; },

    // Null (no badge) for Active patients, and for an older backend that doesn't
    // send patStatus yet — so this is safe to ship ahead of the service.
    patientStatusBadge() {
      return patientStatusLabel(this.patient && this.patient.patStatus);
    },

    patientInitials() {
      if (!this.patient) return '';
      const f = (this.patient.fName || '').charAt(0);
      const l = (this.patient.lName || '').charAt(0);
      return (f + l).toUpperCase() || '??';
    },
    // The server's `selectedPlan` carries no dateTP — only the `plans` list does — so
    // resolve it from there by the selected TreatPlanNum.
    selectedPlanDateTP() {
      const p = (this.plans || []).find((x) => x.treatPlanNum === this.selectedTreatPlanNum);
      const d = p && p.dateTP;
      return d && d !== '0001-01-01' ? d : null;
    },

    patientAvatarStyle() {
      const init = this.patientInitials;
      const hash = (init.charCodeAt(0) || 0) + (init.charCodeAt(1) || 0);
      const hues = [186, 214, 262, 28, 170, 330, 42, 200, 280];
      const h = hues[hash % hues.length];
      return { background: `oklch(0.68 0.11 ${h})` };
    },
  },

  watch: {
    internalNote: {
      immediate: true,
      handler(newVal) {
        this.internalNoteEdit = newVal || '';
        this.lastSavedNote = newVal || '';
      },
    },
  },

  methods: {
    ...mapActions('txPresenter', ['closeDrawer', 'selectPlan', 'saveInternalNote']),

    onClose() { this.closeDrawer(); },

    async onSelectPlan(treatPlanNum) {
      if (treatPlanNum === this.selectedTreatPlanNum) return;
      try { await this.selectPlan({ treatPlanNum }); } catch (e) { /* in state */ }
    },

    async onSaveNote() {
      const trimmed = (this.internalNoteEdit || '').trim();
      const last = (this.lastSavedNote || '').trim();
      if (trimmed === last) return;
      try {
        await this.saveInternalNote({
          internalNote: trimmed || null,
          setBy: this.$store.state.customer.employeeID || null,
        });
        this.lastSavedNote = this.internalNoteEdit;
      } catch (e) { /* in state */ }
    },

    onBuild() {
      if (!this.patient || !this.selectedTreatPlanNum) return;
      this.$router.push({
        name: 'TxPresenterBuilder',
        params: {
          patNum: String(this.patient.patNum),
          treatPlanNum: String(this.selectedTreatPlanNum),
        },
      });
    },

    // One-tap chairside present — skip the Builder, jump straight into
    // full-screen Present Mode (a standalone route that bootstraps its own
    // plan data). Inherits the practice "house style" display defaults.
    onPresent() {
      if (!this.patient || !this.selectedTreatPlanNum) return;
      this.$router.push({
        name: 'TxPresenterPresentMode',
        params: {
          patNum: String(this.patient.patNum),
          treatPlanNum: String(this.selectedTreatPlanNum),
        },
      });
    },

    formatDate(d) {
      if (!d || d === '0001-01-01') return '—';
      return moment(d).format('MMM D, YYYY');
    },

    // True ONLY when the heading is literally this plan's own DateTP — offices routinely
    // name plans "8-2-2026". Used to suppress a redundant "8-2-2026 · Aug 2" chip.
    // STRICT parse against an explicit format list: a bare moment(heading) falls through to
    // Date-parse heuristics, which warns and false-positives on any string with numbers in it.
    // Strict M/D already match both 1- and 2-digit forms, so "08-02-2026" is covered too.
    headingIsSameDate(heading, dateTP) {
      if (!heading || !dateTP) return false;
      const m = moment(
        String(heading).trim(),
        ['M-D-YYYY', 'M/D/YYYY', 'M.D.YYYY', 'YYYY-M-D', 'M-D-YY', 'M/D/YY'],
        true,
      );
      return m.isValid() && m.format('YYYY-MM-DD') === dateTP;
    },

    // Compact chip date, or null to render nothing. Matching on the exact DAY is deliberate:
    // a plan headed "8-2-2026" whose real DateTP is Aug 4 is precisely the discrepancy the
    // office needs to see, so that one keeps its date.
    chipDate(plan) {
      const d = plan && plan.dateTP;
      if (!d || d === '0001-01-01') return null;
      if (this.headingIsSameDate(plan.heading, d)) return null;
      const m = moment(d, 'YYYY-MM-DD', true);
      if (!m.isValid()) return null;
      // Year only when it isn't the current one — "Aug 2" is ambiguous on a 2024 plan
      // sitting next to 2026 ones, and stale plans are what these offices accumulate.
      return m.year() === moment().year() ? m.format('MMM D') : m.format('MMM D, YYYY');
    },
    formatMoney(n) {
      if (n === null || n === undefined) return '—';
      const num = parseFloat(n) || 0;
      return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
  },
};
</script>

<style scoped>
.tx-drawer {
  --tx-bg: #f7f8fa;
  --tx-surface: #ffffff;
  --tx-surface2: #f1f3f6;
  --tx-border: #e4e7ec;
  --tx-border-strong: #cdd3dc;
  --tx-text: #0f172a;
  --tx-text-mute: #475569;
  --tx-text-dim: #94a3b8;
  --tx-accent: #0f6e63;
  --tx-accent-soft: #e6f1ef;
  --tx-accent-ink: #094840;

  width: 380px;
  flex-shrink: 0;
  background: var(--tx-surface);
  border: 1px solid var(--tx-border);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  color: var(--tx-text);
  font-size: 14px;
  position: sticky;
  top: 16px;
  max-height: calc(100vh - 110px);
}

.tx-tab-num { font-variant-numeric: tabular-nums; }

/* ─── Header ─── */
.tx-drawer-header {
  padding: 16px 20px 14px;
  border-bottom: 1px solid var(--tx-border);
  flex-shrink: 0;
}
.tx-drawer-id {
  font-size: 11px;
  color: var(--tx-text-dim);
  font-family: 'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace;
}
.tx-drawer-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  flex-shrink: 0;
  letter-spacing: -0.2px;
}
.tx-drawer-name {
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.2px;
}
.tx-drawer-meta {
  font-size: 12px;
  color: var(--tx-text-mute);
  margin-top: 2px;
}
/* Non-Active OD patient status (Inactive / Archived / Deceased / …). Amber, not
   red — it's information the TC should see before sending, not an error. */
.tx-status-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 10px;
  background: #fdf3d7;
  color: #8a6d1a;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  vertical-align: middle;
}

/* ─── Plan badges ─── */
.tx-plan-badges {
  margin-top: 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  /* Cap the chip list so a patient with many saved plans (e.g. "+58 saved")
     can't grow the header past the drawer and push the action footer off the
     clipped panel. Scroll within this area instead. */
  max-height: min(150px, 30vh);
  overflow-y: auto;
  padding-right: 4px;
}
.tx-plan-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: var(--tx-surface2);
  border: 1px solid var(--tx-border);
  border-radius: 999px;
  font-size: 12px;
  color: var(--tx-text-mute);
  cursor: pointer;
  transition: all .12s;
  max-width: 100%;
}
.tx-plan-badge:hover:not(:disabled) {
  background: #ebebeb;
  border-color: var(--tx-border-strong);
}
.tx-plan-badge.active {
  background: var(--tx-accent-soft);
  border-color: var(--tx-accent);
  color: var(--tx-accent-ink);
  font-weight: 550;
}
.tx-plan-badge:disabled { opacity: 0.5; cursor: default; }
.tx-active-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #14b8a6;
  flex-shrink: 0;
}
.tx-plan-badge-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}
.tx-plan-badge-date {
  font-size: 11px;
  color: var(--tx-text-dim);
  /* The label ellipsizes, the date never does — flexbox shrinks the label first. */
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
}
.tx-plan-badge.active .tx-plan-badge-date {
  /* --tx-text-dim is illegible on --tx-accent-soft. */
  color: var(--tx-accent-ink);
  opacity: 0.65;
}
.tx-active-suffix {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--tx-accent);
  font-weight: 600;
}

/* ─── Loading / error ─── */
.tx-drawer-loading {
  text-align: center;
  padding: 32px;
  color: var(--tx-text-mute);
}
.tx-drawer-error {
  margin: 12px 20px;
  padding: 10px 12px;
  background: #fbe0e4;
  color: #be123c;
  border-radius: 8px;
  font-size: 13px;
}

/* ─── Body sections ─── */
.tx-drawer-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 16px 20px;
}
.tx-section {
  margin-bottom: 18px;
}
.tx-section-label {
  font-size: 11px;
  color: var(--tx-text-mute);
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-weight: 550;
  margin-bottom: 8px;
}
.tx-section-status {
  font-size: 11px;
  color: var(--tx-text-dim);
}
.tx-section-content {
  color: var(--tx-text);
}
.tx-section-subtext {
  margin-top: 4px;
  font-size: 12px;
  color: var(--tx-text-dim);
}

/* ─── Financials box ─── */
.tx-finbox {
  background: var(--tx-surface2);
  border-radius: 9px;
  padding: 12px 14px;
}
.tx-finrow {
  display: flex;
  align-items: center;
  padding: 3px 0;
  font-size: 13px;
}
.tx-finrow-label {
  color: var(--tx-text-mute);
  flex: 1;
}
.tx-finrow-value {
  color: var(--tx-text);
  font-weight: 450;
}
.tx-finrow-bold {
  font-size: 15px;
  font-weight: 600;
  color: var(--tx-accent-ink);
}

/* ─── Internal note ─── */
.tx-note-textarea {
  width: 100%;
  padding: 10px 12px;
  border-radius: 7px;
  border: 1px solid var(--tx-border);
  background: var(--tx-surface);
  font-size: 13px;
  font-family: inherit;
  color: var(--tx-text);
  outline: none;
  resize: vertical;
  min-height: 70px;
  transition: border-color .12s, box-shadow .12s;
}
.tx-note-textarea:focus {
  border-color: var(--tx-accent);
  box-shadow: 0 0 0 3px var(--tx-accent-soft);
}
.tx-note-empty:not(:focus) {
  background: #fdf7e3;
  border-color: #f2e2a8;
}
.tx-note-textarea:disabled { opacity: 0.6; cursor: not-allowed; }

/* ─── Team rows ─── */
.tx-team-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
}
.tx-role-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  color: #fff;
  flex-shrink: 0;
  letter-spacing: -0.2px;
}
.tx-role-avatar-dentist { background: #6b5ed3; }
.tx-role-avatar-presenter { background: #c66a3d; }
.tx-role-avatar-empty {
  background: var(--tx-surface2);
  color: var(--tx-text-dim);
}
.tx-team-name {
  font-size: 13px;
  flex: 1;
  color: var(--tx-text);
}
.tx-team-role {
  font-size: 11px;
  color: var(--tx-text-dim);
}
.tx-btn-mini {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 5px;
  border: 1px solid var(--tx-border);
  background: var(--tx-surface);
  color: var(--tx-text-mute);
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all .12s;
}
.tx-btn-mini:hover:not(:disabled) {
  background: var(--tx-accent-soft);
  color: var(--tx-accent-ink);
  border-color: var(--tx-accent);
}
.tx-btn-mini:disabled { opacity: 0.5; cursor: not-allowed; }

/* ─── Buttons ─── */
.tx-icon-btn {
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--tx-text-mute);
  cursor: pointer;
  transition: all .12s;
}
.tx-icon-btn:hover { background: var(--tx-surface2); color: var(--tx-text); }
.tx-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid var(--tx-border);
  background: var(--tx-surface);
  color: var(--tx-text);
  cursor: pointer;
  transition: all .12s;
}
.tx-btn:hover:not(:disabled) {
  background: var(--tx-surface2);
  border-color: var(--tx-border-strong);
}
.tx-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.tx-btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 550;
  border: 1px solid var(--tx-accent);
  background: var(--tx-accent);
  color: #fff;
  cursor: pointer;
  transition: all .12s;
}
.tx-btn-primary:hover:not(:disabled) {
  background: var(--tx-accent-ink);
  border-color: var(--tx-accent-ink);
}
.tx-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.tx-btn-danger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid #fbe0e4;
  background: #fbe0e4;
  color: #be123c;
  cursor: pointer;
  transition: all .12s;
}
.tx-btn-danger:hover:not(:disabled) { background: #f8c7cd; }
.flex-grow-1 { flex-grow: 1; }
.flex-grow-1-3 { flex: 1.3; }

/* ─── Footer ─── */
.tx-drawer-footer {
  padding: 12px 16px;
  border-top: 1px solid var(--tx-border);
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex-shrink: 0;
}
/* "Present on Tablet" sits on its own full-width row beneath Build + Present
   so the long label doesn't crowd the ~360px drawer. */
.tx-btn-tablet { flex: 1 0 100%; }

/* ─── Modal ─── */
.tx-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}
.tx-modal {
  width: 440px;
  max-width: 92vw;
  background: var(--tx-surface);
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
}
.tx-modal-header {
  display: flex;
  align-items: center;
  padding: 14px 18px;
  border-bottom: 1px solid var(--tx-border);
  font-size: 15px;
  font-weight: 600;
}
.tx-modal-body {
  padding: 16px 18px;
}
.tx-modal-desc {
  font-size: 13px;
  color: var(--tx-text-mute);
  margin: 0 0 12px;
  line-height: 1.5;
}
.tx-input-full {
  width: 100%;
  padding: 8px 12px;
  border-radius: 7px;
  border: 1px solid var(--tx-border);
  background: var(--tx-surface);
  font-size: 13px;
  font-family: inherit;
  color: var(--tx-text);
  outline: none;
}
.tx-input-full:focus {
  border-color: var(--tx-accent);
  box-shadow: 0 0 0 3px var(--tx-accent-soft);
}
.tx-modal-hint {
  font-size: 11px;
  color: var(--tx-text-dim);
  margin: 8px 0 0;
}
.tx-modal-footer {
  padding: 12px 18px;
  border-top: 1px solid var(--tx-border);
  display: flex;
  align-items: center;
  gap: 6px;
}

/* ─── Scrollbar ─── */
.tx-drawer-body::-webkit-scrollbar,
.tx-plan-badges::-webkit-scrollbar { width: 8px; }
.tx-drawer-body::-webkit-scrollbar-track,
.tx-plan-badges::-webkit-scrollbar-track { background: transparent; }
.tx-drawer-body::-webkit-scrollbar-thumb,
.tx-plan-badges::-webkit-scrollbar-thumb {
  background: #cbd3de;
  border-radius: 8px;
  border: 2px solid transparent;
  background-clip: content-box;
}
</style>
